/**
 * Patient Package Redemption — pure logic (PRD.262).
 *
 * Dependency-free helpers for redemption eligibility, quantity validation,
 * balance math, full-redemption detection, and reversal math. Import-free so
 * they are unit-testable with Node's built-in runner and reused by the
 * web-admin redemption service (which does the DB I/O around them).
 */

export type RedemptionItemStatus = 'available' | 'fully_redeemed';

export interface RedemptionEligibilityInput {
  deletedAt: string | null;
  redemptionStatus: string | null;
  paymentStatus: string | null;
  /** Package expiry (YYYY-MM-DD) or null for no expiry. */
  validUntil: string | null;
  itemStatus: string | null;
  remaining: number;
  quantity: number;
  /** Today as YYYY-MM-DD, injected for determinism. */
  today: string;
}

export interface RedemptionEligibility {
  ok: boolean;
  /** Blocking reason when not ok. */
  error?: string;
  /** Non-blocking warning (e.g. partially paid) when ok. */
  warning?: string;
}

/** Quantity must be a finite number > 0 and no greater than the remaining balance. */
export function validateRedeemQuantity(quantity: number, remaining: number): boolean {
  return Number.isFinite(quantity) && quantity > 0 && quantity <= remaining;
}

/**
 * Decide whether a benefit can be redeemed (PRD.262 §4). Only an active,
 * non-expired, non-voided package with an available benefit and sufficient
 * remaining balance is redeemable. Partial payment does NOT block — it returns
 * `ok: true` with a `warning`.
 */
export function checkRedemptionEligibility(
  input: RedemptionEligibilityInput,
): RedemptionEligibility {
  if (input.deletedAt) {
    return { ok: false, error: 'This package is no longer available.' };
  }
  if (input.redemptionStatus === 'voided' || input.paymentStatus === 'voided') {
    return { ok: false, error: 'This package has been voided and cannot be redeemed.' };
  }
  if (input.redemptionStatus === 'suspended') {
    return { ok: false, error: 'This package is suspended and cannot be redeemed.' };
  }
  if (input.redemptionStatus === 'pending_payment') {
    return {
      ok: false,
      error: 'This package is not active yet. Record a payment or activate it before redemption.',
    };
  }
  if (input.redemptionStatus === 'fully_redeemed') {
    return { ok: false, error: 'All benefits in this package have been redeemed.' };
  }
  if (input.redemptionStatus !== 'active') {
    return { ok: false, error: 'This package is not active and cannot be redeemed.' };
  }
  if (input.validUntil && input.validUntil < input.today) {
    return { ok: false, error: `This package expired on ${input.validUntil}.` };
  }
  if (input.itemStatus && input.itemStatus !== 'available') {
    return { ok: false, error: 'This benefit has been fully used.' };
  }
  if (input.remaining <= 0) {
    return { ok: false, error: 'This benefit has no remaining balance.' };
  }
  if (!validateRedeemQuantity(input.quantity, input.remaining)) {
    return {
      ok: false,
      error: 'This benefit no longer has enough remaining quantity. Please refresh and try again.',
    };
  }
  const warning =
    input.paymentStatus === 'partially_paid' ? 'This package is partially paid.' : undefined;
  return { ok: true, warning };
}

export interface ItemBalance {
  redeemedQuantity: number;
  remainingQuantity: number;
  status: RedemptionItemStatus;
}

/** Apply a redemption to an item's balance (PRD.262 §6). */
export function applyRedemptionToBalance(
  redeemed: number,
  remaining: number,
  quantity: number,
): ItemBalance {
  const redeemedQuantity = redeemed + quantity;
  const remainingQuantity = Math.max(0, remaining - quantity);
  return {
    redeemedQuantity,
    remainingQuantity,
    status: remainingQuantity <= 0 ? 'fully_redeemed' : 'available',
  };
}

/** Restore an item's balance when a redemption is reversed (PRD.262 §11). */
export function applyReversalToBalance(
  redeemed: number,
  remaining: number,
  quantity: number,
): ItemBalance {
  const redeemedQuantity = Math.max(0, redeemed - quantity);
  const remainingQuantity = remaining + quantity;
  // Returning any quantity makes the benefit available again.
  return { redeemedQuantity, remainingQuantity, status: 'available' };
}

/** A benefit is fully redeemed when nothing remains. */
export function isItemFullyRedeemed(remaining: number): boolean {
  return remaining <= 0;
}

/**
 * Whether every active benefit in a package is fully redeemed — used to flip
 * the package's redemption_status to 'fully_redeemed' (PRD.262 §6).
 */
export function allItemsFullyRedeemed(items: readonly { remainingQuantity: number }[]): boolean {
  return items.length > 0 && items.every((i) => i.remainingQuantity <= 0);
}

/** Covered amount for a redemption line = unit price * quantity (>= 0). */
export function computeCoveredAmount(
  unitPrice: number | null | undefined,
  quantity: number,
): number {
  const price = unitPrice ?? 0;
  const amount = price * quantity;
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
}
